import ModalDialog, { ModalTransition } from '@atlaskit/modal-dialog';
import React, { useCallback, useContext } from 'react';
import { BonusRecordTaggingContext } from './BonusRecordTaggingProvider';
import Form from '../../../../kingpin/forms/Form';
import FormHeader from '../../../../kingpin/forms/FormHeader';
import FormContent from '../../../../kingpin/forms/FormContent';
import Typography from '../../../../kingpin/atoms/Typography';
import Row from '../../../Common/Row';
import Button from '../../../../kingpin/atoms/Button';
import Inputs from '../../../Inputs';
import addBonusTag from '../../api/addBonusTag';
import removeBonusTag from '../../api/removeBonusTag';
import CloudFunctionClientContext from '../../../../contexts/CloudFunctionClientContext';
import { BonusOverviewContext } from '../../providers/BonusOverviewProvider';
import getRecordTaggingFieldKey from './getRecordTaggingFieldKey';

const UpdatePopup = ({ close }: { close: () => void }) => {
  const {
    comment,
    setComment,
    gridConfig,
    selectedDocumentIds,
    taggedDocumentIds,
    setTaggedDocumentIds,
    configuredTag,
  } = useContext(BonusRecordTaggingContext);
  const { bonusOverview, currentMode, setFieldTagCount } =
    useContext(BonusOverviewContext);
  const { api } = useContext(CloudFunctionClientContext);
  const [isSaving, setIsSaving] = React.useState(false);

  const onCommentChanged = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      setComment(event.target.value);
    },
    [setComment],
  );

  const onSave = useCallback(() => {
    if (!bonusOverview || !gridConfig) return;

    // The tags API is incremental (add/remove), unlike the old full-replace
    // exclusions PUT — so diff the selection against the persisted tagged set.
    const toAdd = selectedDocumentIds.filter(
      (id) => !taggedDocumentIds.includes(id),
    );
    const toRemove = taggedDocumentIds.filter(
      (id) => !selectedDocumentIds.includes(id),
    );

    const { id: bonusProgramId } = bonusOverview.bonusProgram;
    const period = bonusOverview.bonusPeriod.id;
    const { driver } = bonusOverview;

    setIsSaving(true);

    const requests = [];
    if (toAdd.length) {
      requests.push(
        addBonusTag({
          api,
          bonusProgramId,
          period,
          driver,
          tag: configuredTag,
          recordIds: toAdd,
          dataType: gridConfig.dataType,
          comment,
        }),
      );
    }
    if (toRemove.length) {
      requests.push(
        removeBonusTag({
          api,
          bonusProgramId,
          period,
          driver,
          tag: configuredTag,
          recordIds: toRemove,
          dataType: gridConfig.dataType,
        }),
      );
    }

    Promise.all(requests).then((responses) => {
      if (responses.every((r) => r.ok)) {
        setTaggedDocumentIds(selectedDocumentIds);
        // Optimistically reflect the new count on the expectations list (the
        // backend `tags` reindex is delayed).
        if (currentMode.taggableField) {
          setFieldTagCount(
            getRecordTaggingFieldKey({
              fieldType: currentMode.fieldType ?? 'criteria',
              fieldName: currentMode.taggableField.name,
            }),
            selectedDocumentIds.length,
          );
        }
      }
      setIsSaving(false);
      close();
    });
  }, [
    api,
    bonusOverview,
    close,
    comment,
    configuredTag,
    currentMode.taggableField,
    currentMode.fieldType,
    taggedDocumentIds,
    gridConfig,
    selectedDocumentIds,
    setFieldTagCount,
    setTaggedDocumentIds,
  ]);

  return (
    <Form>
      <FormHeader title={'Update exclusions'} onClose={close} />
      <FormContent>
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 4 }}>
            <Typography.Body type={'Label'}>Comment</Typography.Body>
          </div>
          <Inputs.TextArea
            id={'update-exclusion-comment'}
            value={comment}
            placeholder={'Enter Comment'}
            onChange={onCommentChanged}
          />
        </div>
      </FormContent>
      <Row spaceBetween>
        <Button
          size={'Small'}
          type={'Secondary'}
          label={'Cancel'}
          onClick={close}
          isDisabled={isSaving}
        />
        <Button
          size={'Small'}
          type={'Primary'}
          label={'Save'}
          onClick={onSave}
          testId={'confirm-update-exclusions'}
          isLoading={isSaving}
          isDisabled={!comment}
        />
      </Row>
    </Form>
  );
};

const Gate = ({ isOpen, close }: { isOpen: boolean; close: () => void }) => {
  return (
    <ModalTransition>
      {isOpen && (
        <ModalDialog
          onClose={close}
          shouldScrollInViewport={false}
          autoFocus={false}
        >
          <UpdatePopup close={close} />
        </ModalDialog>
      )}
    </ModalTransition>
  );
};
export default Gate;
